/**
 * useRawMaterialStockHistory — picker + paginated ledger for the
 * Manufacturing > Raw Material Stock History screen. Mirror of
 * useVariantStockHistory but simpler (no axis lookup, no nested product
 * picker). Charts are a follow-up; the page renders ledger only for v1.
 */

import { useCallback, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES } from '../constants/routes.constants'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'
import { useCurrentYearStock, type UseCurrentYearStockReturn } from './useCurrentYearStock'

export interface MaterialPickerEntry {
  readonly raw_material_id: string
  readonly raw_material_name: string
  readonly unit_label: string | null
}

export interface MaterialMovementDto {
  readonly id: string
  readonly raw_material_id: string
  readonly raw_material_name: string | null
  readonly unit_label: string | null
  readonly direction: 'in' | 'out'
  readonly movement_type: string
  readonly quantity_units: number
  readonly note: string | null
  readonly occurred_at: string
  readonly created_at: string
}

interface LedgerBody {
  readonly success: true
  readonly data: {
    readonly materials: MaterialPickerEntry[]
    readonly ledger: { readonly items: MaterialMovementDto[]; readonly total: number }
  }
}

export function useRawMaterialStockHistory(): {
  readonly materialId: string | null
  readonly setMaterialId: (id: string | null) => void
  readonly materials: MaterialPickerEntry[]
  readonly ledger: MaterialMovementDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly chart: UseCurrentYearStockReturn
} {
  const { authedFetch } = useAuth()
  const [materialId, setMaterialId] = useState<string | null>(null)

  const paged = usePaginatedList<MaterialMovementDto, { materials: MaterialPickerEntry[] }>({
    deps: [materialId],
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        let url = `${APIROUTES.MANUFACTURINGRAWMATERIALSTOCKHISTORY}?page=${page}&pageSize=${pageSize}`
        if (materialId !== null && materialId !== '') url += `&materialId=${encodeURIComponent(materialId)}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as LedgerBody
        return {
          items: body.data.ledger.items,
          total: body.data.ledger.total,
          extras: { materials: body.data.materials }
        }
      },
      [authedFetch, materialId]
    )
  })

  const chart = useCurrentYearStock('material', materialId)

  return {
    materialId,
    setMaterialId,
    materials: paged.extras?.materials ?? [],
    ledger: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    chart
  }
}
